import { useCallback, useState } from "react";
import { Camera, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, money } from "@/lib/api";
import { ErrorBox, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { CouponTicket } from "@/components/RxSheet";
import { Scanner } from "@/pages/Register";

export function CouponRedeem() {
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(false);
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const look = async (v) => { setError(""); try { setC((await api.get(`/rx/provider/coupons/${encodeURIComponent(v.trim())}`)).data); } catch (e) { setC(null); setError(errMsg(e)); } };
  const onScan = useCallback((t) => { setScan(false); setCode(t); look(t); }, []);
  const use = async () => {
    if (!window.confirm(`Mark coupon ${c.code} for ${c.member_name} as used? This cannot be undone.`)) return;
    try { setC((await api.post(`/rx/provider/coupons/${c.code}/use`)).data); toast.success("Coupon marked as used"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mx-auto max-w-md space-y-4">
      <PageHead eyebrow="Doctor Portal" title="Redeem coupon" sub="Any GIA doctor can honour a free appointment coupon." />
      <form className="surface space-y-3 p-4" onSubmit={(e) => { e.preventDefault(); look(code); }}>
        <div className="flex gap-2">
          <input className="field py-3 font-mono uppercase" placeholder="GC-XXXXXXXX" value={code} onChange={(e) => setCode(e.target.value)} required data-testid="coupon-code-input" />
          <button type="button" className="btn-secondary" onClick={() => setScan((s) => !s)} data-testid="coupon-scan-button"><Camera size={16} /></button>
          <button className="btn-primary" data-testid="coupon-lookup-button">Check</button>
        </div>
        {scan && <Scanner onResult={onScan} />}
      </form>
      <ErrorBox error={error} />
      {c && (
        <div className="space-y-3">
          <CouponTicket c={c} />
          {c.status === "AVAILABLE" ? <button className="btn-primary w-full py-4 text-base" onClick={use} data-testid="mark-coupon-used-button"><CheckCircle2 size={18} /> Mark as used</button>
            : <p className="text-center text-sm text-slate-500" data-testid="coupon-unavailable">This coupon is {c.status.toLowerCase()}.</p>}
        </div>
      )}
    </div>
  );
}

export function CouponClaims() {
  const { data, reload } = useFetch("/rx/provider/claims");
  const [sel, setSel] = useState([]);
  if (!data) return <Spinner />;
  const claimable = data.items.filter((c) => ["UNCLAIMED", "REJECTED"].includes(c.claim_status));
  const submit = async () => {
    try { const r = await api.post("/rx/provider/claims/submit", { codes: sel }); toast.success(`${r.data.submitted} coupon(s) submitted`); setSel([]); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Doctor Portal" title="Coupon claims" actions={<button className="btn-primary" disabled={!sel.length} onClick={submit} data-testid="submit-claims-button"><Send size={14} /> Submit {sel.length || ""} for payment</button>} />
      <div className="grid gap-3 sm:grid-cols-5">{["UNCLAIMED", "SUBMITTED", "APPROVED", "PAID", "REJECTED"].map((s) => (
        <div key={s} className="surface p-4" data-testid={`claims-total-${s}`}><p className="label">{s}</p><p className="text-xl font-extrabold">{money(data.totals[s]?.amount || 0)}</p><p className="text-xs text-slate-500">{data.totals[s]?.count || 0} coupons</p></div>
      ))}</div>
      <div className="surface overflow-x-auto"><table className="data-table" data-testid="claims-table">
        <thead><tr><th><input type="checkbox" checked={claimable.length > 0 && sel.length === claimable.length} onChange={(e) => setSel(e.target.checked ? claimable.map((c) => c.code) : [])} data-testid="claims-select-all" /></th><th>Coupon</th><th>Member</th><th>Package</th><th>Used</th><th>Fee</th><th>Claim</th></tr></thead>
        <tbody>{data.items.map((c) => (
          <tr key={c.code}><td>{["UNCLAIMED", "REJECTED"].includes(c.claim_status) && <input type="checkbox" checked={sel.includes(c.code)} onChange={(e) => setSel(e.target.checked ? [...sel, c.code] : sel.filter((x) => x !== c.code))} data-testid={`claim-select-${c.code}`} />}</td>
            <td className="font-mono text-xs">{c.code}</td><td>{c.member_name}</td><td className="text-xs">{c.package_name}</td><td className="text-xs">{fmtDateTime(c.used_at)}</td><td>{money(c.fee)}</td>
            <td><StatusBadge status={c.claim_status} />{c.claim_reject_reason && <p className="text-[10px] text-red-600">{c.claim_reject_reason}</p>}{c.claim_paid_reference && <p className="text-[10px]">Ref {c.claim_paid_reference}</p>}</td></tr>
        ))}</tbody>
      </table></div>
    </div>
  );
}
